import { useState } from 'react';
import { PrecalcTest } from '../data/precalcTests';

interface Props {
  test: PrecalcTest;
  onBack: () => void;
}

export default function StateTestCard({ test, onBack }: Props) {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [showSolutions, setShowSolutions] = useState<Record<number, boolean>>({});

  const handleAnswer = (questionId: number, option: string) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length < test.questions.length) {
      const unanswered = test.questions.length - Object.keys(answers).length;
      if (!window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`)) return;
    }
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
    setShowSolutions({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSolution = (id: number) => {
    setShowSolutions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const score = submitted
    ? test.questions.filter(q => answers[q.id] === q.correctAnswer).length
    : 0;
  const pct = submitted ? Math.round((score / test.questions.length) * 100) : 0;

  const getGrade = () => {
    if (pct >= 90) return { label: 'Excellent!', color: 'text-emerald-600' };
    if (pct >= 75) return { label: 'Proficient', color: 'text-teal-600' };
    if (pct >= 60) return { label: 'Developing', color: 'text-amber-600' };
    return { label: 'Needs Practice', color: 'text-rose-600' };
  };

  return (
    <div className="max-w-4xl mx-auto px-4 pb-16">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-6 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-arrow-left-line text-xl"></i>
        Back to Test Selection
      </button>

      {/* Header */}
      <div className={`bg-gradient-to-br ${test.color} rounded-2xl p-8 text-white mb-8 shadow-xl`}>
        <div className="flex items-center gap-4 mb-3">
          <span className="text-5xl">{test.flag}</span>
          <div>
            <h2 className="text-3xl font-bold">{test.state} Precalculus Practice Test</h2>
            <p className="text-white/80 mt-1">{test.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 mt-4 text-sm text-white/90">
          <span className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-lg"></i>
            {test.questions.length} Questions
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-star-line text-lg"></i>
            60 Points
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-time-line text-lg"></i>
            ~90 minutes
          </span>
        </div>
      </div>

      {/* Score Banner */}
      {submitted && (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 mb-8 text-center">
          <div className="text-6xl font-bold text-slate-800 mb-2">{score}/{test.questions.length}</div>
          <div className="text-2xl font-semibold mb-1">{pct}%</div>
          <div className={`text-xl font-bold ${getGrade().color} mb-4`}>{getGrade().label}</div>
          <div className="flex justify-center gap-4">
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-2"></i>Retake Test
            </button>
            <button
              onClick={() => setShowSolutions(Object.fromEntries(test.questions.map(q => [q.id, true])))}
              className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-eye-line mr-2"></i>Show All Solutions
            </button>
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-6">
        {test.questions.map((q, idx) => {
          const selected = answers[q.id];
          const isCorrect = submitted && selected === q.correctAnswer;
          const isWrong = submitted && selected && selected !== q.correctAnswer;
          const showSol = showSolutions[q.id];

          return (
            <div
              key={q.id}
              className={`bg-white rounded-2xl p-6 shadow-sm border-2 transition-all ${
                isCorrect ? 'border-emerald-300 bg-emerald-50/30' :
                isWrong ? 'border-rose-300 bg-rose-50/30' :
                'border-slate-200'
              }`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  isCorrect ? 'bg-emerald-500 text-white' :
                  isWrong ? 'bg-rose-500 text-white' :
                  'bg-gradient-to-br from-teal-600 to-emerald-600 text-white'
                }`}>
                  {submitted ? (isCorrect ? <i className="ri-check-line"></i> : isWrong ? <i className="ri-close-line"></i> : idx + 1) : idx + 1}
                </div>
                <p className="text-slate-800 font-medium leading-relaxed">{q.question}</p>
              </div>

              <div className="grid grid-cols-1 gap-2 ml-13 pl-13">
                {q.options.map((opt) => {
                  const isSelected = selected === opt;
                  const isCorrectOpt = submitted && opt === q.correctAnswer;
                  const isWrongOpt = submitted && isSelected && opt !== q.correctAnswer;

                  return (
                    <button
                      key={opt}
                      onClick={() => handleAnswer(q.id, opt)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer whitespace-normal ${
                        isCorrectOpt
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                          : isWrongOpt
                          ? 'border-rose-500 bg-rose-50 text-rose-800'
                          : isSelected
                          ? 'border-teal-500 bg-teal-50 text-teal-800'
                          : 'border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 text-slate-700'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {isCorrectOpt && submitted && <i className="ri-check-line text-emerald-600 flex-shrink-0"></i>}
                        {isWrongOpt && <i className="ri-close-line text-rose-600 flex-shrink-0"></i>}
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </div>

              {submitted && (
                <div className="mt-4 ml-13 pl-13">
                  <button
                    onClick={() => toggleSolution(q.id)}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`ri-${showSol ? 'eye-off' : 'eye'}-line`}></i>
                    {showSol ? 'Hide' : 'Show'} Solution
                  </button>
                  {showSol && (
                    <div className="mt-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      <span className="font-semibold text-teal-700 block mb-1">Solution:</span>
                      {q.solution}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit Button */}
      {!submitted && (
        <div className="mt-10 text-center">
          <div className="text-sm text-slate-500 mb-4">
            {Object.keys(answers).length} of {test.questions.length} questions answered
          </div>
          <button
            onClick={handleSubmit}
            className="px-10 py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line mr-2"></i>Submit Test
          </button>
        </div>
      )}
    </div>
  );
}
